// LayoutEditorInsertSequence.jsx

import React from 'react';

// Components
import PageEditorInsertButton from '../insertbutton/PageEditorInsertButton';

/**
 * LayoutEditorInsertSequence
 *
 * Renders insert buttons before, between, and after a sequence of layout children.
 *
 * @param {string[]} childKeys - Ordered child keys for the rendered sequence.
 * @param {function|null} getInsertButtonProps - Returns extra props for a given insert button index.
 * @param {string} keyPrefix - Prefix used to build stable insert button keys.
 * @param {function} onInsertAt - Called with the target insert index.
 * @param {string} orientation - Whether the insert buttons are horizontal or vertical.
 * @param {function} renderChild - Renders the child node for a given key and index.
 *
 * @returns {ReactNode} The rendered insert sequence.
 */
const LayoutEditorInsertSequence = ({
	childKeys = [],
	getInsertButtonProps = null,
	keyPrefix = '',
	onInsertAt,
	orientation = 'vertical',
	renderChild,
}) => {

	const resolvedChildKeys = Array.isArray(childKeys) ? childKeys : [];

	// ----- RENDER ----- //

	return (
		<>
			{Array.from({ length: resolvedChildKeys.length + 1 }).map((_, index) => {
				const insertButtonProps = typeof getInsertButtonProps === 'function'
					? getInsertButtonProps(index) || {}
					: {};

				return (
					<React.Fragment key={`${keyPrefix}:insert-sequence:${index}`}>
						<PageEditorInsertButton
							{...insertButtonProps}
							orientation={orientation}
							onClick={() => onInsertAt?.(index)}
						/>

						{index < resolvedChildKeys.length
							? renderChild?.(resolvedChildKeys[index], index)
							: null}
					</React.Fragment>
				);
			})}
		</>
	);
};

// Dev Tools
LayoutEditorInsertSequence.displayName = 'LayoutEditorInsertSequence';

export default LayoutEditorInsertSequence;
